import type { ScheduleIssueCounts } from "../types";

/**
 * Result of evaluating the [Run Scheduler] button state for a given schedule.
 * Modeled as a discriminated union on `enabled` so consumers can narrow
 * exhaustively at compile time — `reason` is required on the disabled branch;
 * `errorBadge` / `warningBadge` are optional on the enabled branch.
 *
 * The schedule home header consumes this directly to decide button styling,
 * tooltip copy, and the issue chips.
 */
export type RunGate =
  | {
      /** Scheduler cannot be invoked yet (schedule still loading). */
      enabled: false;
      /**
       * Human-readable explanation surfaced as a `title` tooltip / SR text
       * when the gate is disabled. Always populated for the disabled branch.
       */
      reason: string;
    }
  | {
      /** Scheduler can be invoked. */
      enabled: true;
      /**
       * Number of outstanding Stage 1/2 errors. Runs are best-effort and do
       * NOT block on errors — the items behind them are ignored (unmatched
       * requests go unplaced) — but the count is surfaced as a chip so the
       * user knows the run will skip them. Undefined when there are no errors.
       */
      errorBadge?: number;
      /**
       * Number of non-blocking warnings. Surfaced as a chip on the enabled
       * button so users notice them before running. Undefined when there are
       * no warnings.
       */
      warningBadge?: number;
    };

/**
 * Pure function that maps a schedule's `issueCounts` to the run-button gate
 * state. Two branches:
 *
 *  - `undefined` (schedule not yet loaded) → disabled with a loading reason.
 *  - loaded → enabled. Errors no longer block: the scheduler runs best-effort
 *    and ignores the erroring items. Outstanding errors and warnings are each
 *    surfaced via {@link RunGate.errorBadge} / {@link RunGate.warningBadge} so
 *    the button can render chips.
 *
 * Kept as a standalone pure helper so the rendering component stays trivial
 * and the gating rules are exhaustively unit-tested in isolation.
 */
export function computeRunGate(
  issueCounts: ScheduleIssueCounts | undefined,
): RunGate {
  if (!issueCounts) {
    return { enabled: false, reason: "Loading schedule…" };
  }
  return {
    enabled: true,
    errorBadge: issueCounts.errors > 0 ? issueCounts.errors : undefined,
    warningBadge: issueCounts.warnings > 0 ? issueCounts.warnings : undefined,
  };
}
